import { getApiUrl } from "../config";

export interface CreateFeedbackRequest {
  message: string;
}

export interface CreateFeedbackResponse {
  success: boolean;
  message: string;
}

export interface FeedbackItem {
  id: number;
  userId: number;
  message: string;
  status: "pending" | "reviewed";
  createdAt: Date;
  updatedAt: Date;
  user: {
    username: string;
    email: string;
  };
}

export interface GetFeedbackResponse {
  feedback: FeedbackItem[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  hasMore: boolean;
}

export interface UpdateFeedbackStatusRequest {
  status: "pending" | "reviewed";
}

export interface UpdateFeedbackStatusResponse {
  success: boolean;
  feedback: FeedbackItem;
  message: string;
}

export interface GetFeedbackParams {
  page?: number;
  limit?: number;
  status?: "pending" | "reviewed";
  sortBy?: "createdAt" | "status";
  sortOrder?: "asc" | "desc";
}

export const createFeedback = async (data: CreateFeedbackRequest): Promise<CreateFeedbackResponse> => {
  const res = await fetch(getApiUrl("/api/feedback"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(data),
  });

  if (!res.ok) {
    const errorData = await res.json();
    throw new Error(errorData.message || "Failed to submit feedback");
  }

  return res.json();
};

export const getFeedback = async (params: GetFeedbackParams = {}): Promise<GetFeedbackResponse> => {
  const queryParams = new URLSearchParams();
  
  if (params.page) queryParams.set("page", params.page.toString());
  if (params.limit) queryParams.set("limit", params.limit.toString());
  if (params.status) queryParams.set("status", params.status);
  if (params.sortBy) queryParams.set("sortBy", params.sortBy);
  if (params.sortOrder) queryParams.set("sortOrder", params.sortOrder);

  const res = await fetch(getApiUrl(`/api/feedback?${queryParams.toString()}`), {
    method: "GET",
    credentials: "include",
  });

  if (!res.ok) {
    const errorData = await res.json();
    throw new Error(errorData.message || "Failed to fetch feedback");
  }

  return res.json();
};

export const updateFeedbackStatus = async (
  id: number, 
  data: UpdateFeedbackStatusRequest
): Promise<UpdateFeedbackStatusResponse> => {
  const res = await fetch(getApiUrl(`/api/feedback/${id}`), {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(data),
  });

  if (!res.ok) {
    const errorData = await res.json();
    throw new Error(errorData.message || "Failed to update feedback status");
  }

  return res.json();
};